import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Info, Link2, Loader2, Plug, Unplug } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import ExternalIntegrationsGate from '../components/Integrations/ExternalIntegrationsGate';
import ZohoCard from '../components/Integrations/ZohoCard';
import OutlookCard from '../components/Integrations/OutlookCard';
import SlackMappingCard from '../components/Integrations/SlackMappingCard';
import NotificationPreviewCard from '../components/Integrations/NotificationPreviewCard';
import NotificationDefaultsCard from '../components/Integrations/NotificationDefaultsCard';
import SlackActivityCard from '../components/Integrations/SlackActivityCard';
import BDAssistantRolloutCard from '../components/BD/BDAssistantRolloutCard';

/**
 * External integrations settings (Task 1) — lives under Admin → Identity & Setup
 * → Integrations. The standalone `/app/integrations` route redirects there so
 * OAuth return URLs and bookmarks keep working.
 *
 * The whole surface is wrapped in ExternalIntegrationsGate, so a tenant that may
 * not use integrations sees "page not found" rather than a permission error —
 * a client-tenant admin who types this URL should not learn that a
 * Stemmler-only surface exists. That is presentation only: every endpoint this
 * page calls enforces the same rule server-side.
 *
 * Scope split (BD-01):
 *   Firm / org  — Slack workspace, Zoho CRM install, optional admin Outlook mailbox
 *   Per person  — Outlook for BD Assistant (Settings → BD Assistant); Zoho authorship
 *                 via email→Zoho-user map under the firm token (rollout table below)
 */

interface Installation {
  id: number;
  slack_team_id: string;
  slack_team_name: string | null;
  bot_user_id: string | null;
  scopes: string[];
  installed_at: string;
  installed_by: number | null;
  installed_by_name: string | null;
  active: boolean;
}

interface SlackStatus {
  configured: boolean;
  missing_config: string[];
  connected: boolean;
  installation: Installation | null;
  requested_scopes: string[];
  redirect_uri: string | null;
  inbound_urls?: { events: string | null; interactivity: string | null };
}

const CALLBACK_MESSAGES: Record<string, string> = {
  INVALID_STATE: 'That install link was not valid. Start the connection again.',
  EXPIRED_STATE: 'That install link expired. Start the connection again.',
  MISSING_PARAMS: 'Slack did not send back everything needed to finish. Try again.',
  NOT_PERMITTED: 'External integrations are not enabled for this organization.',
  INCOMPLETE_OAUTH_RESPONSE: 'Slack did not return a bot token. Try connecting again.',
  invalid_code: 'Slack rejected the install code. Start the connection again.',
  access_denied: 'The install was cancelled in Slack.',
};

const SCOPE_PURPOSE: Record<string, string> = {
  'chat:write': 'Send task notifications',
  'im:write': 'Open a direct message with a teammate',
  'im:history': 'Read replies you send back in a thread',
  'users:read': 'List workspace members for mapping',
  'users:read.email': 'Match Slack members to Jarvis users by email',
  'files:read': 'Open a transcript file or voice clip you send in a thread',
};

export const IntegrationsContent: React.FC = () => {
  const [status, setStatus] = useState<SlackStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/integrations/slack/status');
      setStatus(res.data);
    } catch {
      toast.error('Could not load the Slack connection status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const outcome = searchParams.get('slack');
    if (!outcome) return;

    const reason = searchParams.get('reason');
    if (outcome === 'connected') toast.success('Slack connected.');
    else if (outcome === 'cancelled') toast('Slack connection cancelled.');
    else toast.error(CALLBACK_MESSAGES[reason || ''] || 'Could not connect Slack.');

    searchParams.delete('slack');
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
    void load();
  }, [searchParams, setSearchParams, load]);

  const handleConnect = async () => {
    setWorking(true);
    try {
      const res = await api.post('/api/integrations/slack/install', {});
      window.location.href = res.data.authorize_url;
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not start the Slack connection.');
      setWorking(false);
    }
  };

  const handleDisconnect = async () => {
    setWorking(true);
    try {
      await api.delete('/api/integrations/slack');
      toast.success('Slack disconnected.');
      setConfirmDisconnect(false);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not disconnect Slack.');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="integrations-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const install = status?.installation;

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="integrations-content">
      <header className="mb-2">
        <h2 className="text-lg font-semibold text-gray-900">Organization integrations</h2>
        <p className="text-sm text-gray-500 mt-1">
          Firm-wide connections an admin sets once for Stemmler. These are not each person&apos;s
          personal Zoho or Outlook login.
        </p>
      </header>

      <div
        className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
        data-testid="integrations-scope-callout"
      >
        <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <div className="text-xs text-blue-900 space-y-1.5">
          <p className="font-medium">Who connects what</p>
          <ul className="list-disc pl-4 space-y-1 text-blue-800">
            <li>
              <strong>Zoho CRM</strong> — one firm OAuth install. Notes still show as each teammate
              in Zoho via email match (Sync Zoho users in the rollout table below).
            </li>
            <li>
              <strong>Personal Outlook</strong> — each person connects their own mailbox under{' '}
              <a href="/app/settings?tab=bd-assistant" className="underline font-medium text-blue-700">
                Settings → BD Assistant
              </a>
              . The Outlook card on this page is an optional shared org mailbox for admin tools only.
            </li>
            <li>
              <strong>Slack</strong> — one workspace install for the firm.
            </li>
          </ul>
        </div>
      </div>

      <div className="space-y-4">
      <section
        className="bg-white border border-gray-200 rounded-xl p-5"
        data-testid="slack-integration-card"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
              <Plug className="w-5 h-5 text-gray-500" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900">Slack</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Task reminders and check-ins in Slack, with replies syncing back to Jarvis.
              </p>
            </div>
          </div>

          {status?.connected ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium flex-shrink-0">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium flex-shrink-0">
              Not connected
            </span>
          )}
        </div>

        {!status?.configured && (
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 min-w-0">
                <p className="font-medium">Slack is not configured on this server yet.</p>
                <p className="mt-1">
                  The Slack app has to be created in the workspace and its credentials set as
                  environment variables before anyone can connect.
                </p>
                {status?.missing_config?.length ? (
                  <ul className="mt-2 space-y-0.5 font-mono text-[11px]">
                    {status.missing_config.map((key) => (
                      <li key={key}>· {key}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {status?.connected && install && (
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
            <div className="col-span-2 sm:col-span-1">
              <dt className="text-gray-500">Workspace</dt>
              <dd className="text-gray-900 font-medium mt-0.5 truncate">
                {install.slack_team_name || install.slack_team_id}
              </dd>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <dt className="text-gray-500">Connected by</dt>
              <dd className="text-gray-900 font-medium mt-0.5 truncate">
                {install.installed_by_name || 'Unknown'}
                {install.installed_at && (
                  <span className="text-gray-500 font-normal">
                    {' · '}
                    {new Date(install.installed_at).toLocaleDateString()}
                  </span>
                )}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-gray-500 mb-1">What Jarvis can do in this workspace</dt>
              <dd>
                <ul className="space-y-1">
                  {install.scopes.map((scope) => (
                    <li key={scope} className="text-gray-700">
                      <span className="font-mono text-[11px] text-gray-500">{scope}</span>
                      {SCOPE_PURPOSE[scope] && <span> — {SCOPE_PURPOSE[scope]}</span>}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          </dl>
        )}

        {status?.configured && !status?.connected && status.redirect_uri && (
          <p className="mt-4 text-[11px] text-gray-500">
            <Link2 className="w-3 h-3 inline mr-1" />
            The Slack app&apos;s redirect URL must be exactly{' '}
            <span className="font-mono text-gray-700 break-all">{status.redirect_uri}</span>
          </p>
        )}

        {status?.configured && status.inbound_urls?.events && status.inbound_urls?.interactivity && (
          <div
            className={`mt-3 rounded-lg border px-3 py-2.5 text-[11px] ${
              status.connected
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-transparent bg-transparent text-gray-500'
            }`}
            data-testid="slack-inbound-urls"
          >
            {status.connected && (
              <p className="font-medium mb-1.5">
                Required for Mark Done / Change Date / Reply buttons to work.
                Paste these into the Slack app settings (api.slack.com → your app).
                Until they match, outbound notifications still send but every
                button click is ignored.
              </p>
            )}
            <p className="flex items-start">
              <Link2 className="w-3 h-3 inline mr-1 mt-0.5 shrink-0" />
              <span>
                <strong>Event Subscriptions</strong> &rarr; Request URL:{' '}
                <span className="font-mono text-gray-800 break-all">{status.inbound_urls.events}</span>
              </span>
            </p>
            <p className="flex items-start mt-1">
              <Link2 className="w-3 h-3 inline mr-1 mt-0.5 shrink-0" />
              <span>
                <strong>Interactivity &amp; Shortcuts</strong> &rarr; Request URL:{' '}
                <span className="font-mono text-gray-800 break-all">
                  {status.inbound_urls.interactivity}
                </span>
              </span>
            </p>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          {status?.connected ? (
            confirmDisconnect ? (
              <>
                <span className="text-xs text-gray-600 self-center mr-auto">
                  Disconnect Slack? Notifications stop immediately.
                </span>
                <button
                  onClick={() => setConfirmDisconnect(false)}
                  disabled={working}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={working}
                  data-testid="slack-disconnect-confirm"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmDisconnect(true)}
                data-testid="slack-disconnect"
                className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Disconnect
              </button>
            )
          ) : (
            <button
              onClick={handleConnect}
              disabled={!status?.configured || working}
              data-testid="slack-connect"
              title={status?.configured ? undefined : 'Slack is not configured on this server yet'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
              Connect Slack
            </button>
          )}
        </div>
      </section>

      <SlackMappingCard />
      <NotificationPreviewCard />
      <NotificationDefaultsCard />
      <SlackActivityCard />
      <ZohoCard />
      <OutlookCard />

      <section
        className="bg-white border border-gray-200 rounded-xl p-5"
        data-testid="integrations-personal-connections"
      >
        <h2 className="text-sm font-semibold text-gray-900 mb-1">Personal connections (per user)</h2>
        <p className="text-xs text-gray-500 mb-4">
          Who has their own Outlook linked and Zoho email mapped for BD Assistant. People connect
          Outlook themselves in Settings; admins sync Zoho maps and stage rollout here.
        </p>
        <BDAssistantRolloutCard />
      </section>
      </div>
    </div>
  );
};

/** Legacy bookmark / OAuth landing — send into Admin with query string intact. */
const IntegrationsRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set('tab', 'integrations');
  return <Navigate to={`/app/admin?${next.toString()}`} replace />;
};

const Integrations: React.FC = () => (
  <ExternalIntegrationsGate>
    <IntegrationsRedirect />
  </ExternalIntegrationsGate>
);

export default Integrations;
